/*
  AK Bodyfashion — progressieve interacties voor het Shopify-thema.
  Uitgangspunten:
  - Alles werkt eerst als gewone link of gewoon formulier; dit script maakt het alleen prettiger.
  - Varianten, prijzen, voorraad en winkelmand komen altijd van Shopify, nooit uit lokale gegevens.
  - Lokale opslag bewaart alleen favorieten als lijst met product-handles.
  - Iedere sectie initialiseert opnieuw na shopify:section:load in de thema-editor.
*/
(() => {
  'use strict';

  const AK = window.AK || { routes: { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', predictiveSearch: '/search/suggest' }, cartType: 'drawer' };
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const dialog = $('#site-dialog');
  const dialogContent = $('#dialog-content');
  let savedFocus = null;
  let dialogType = '';
  let toastTimer;

  /* ---------- Hulpmiddelen ---------- */

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  async function getText(url, options = {}) {
    const res = await fetch(url, { credentials: 'same-origin', ...options });
    if (!res.ok) throw Object.assign(new Error('HTTP ' + res.status), { status: res.status });
    return res.text();
  }

  function toast(message) {
    const el = $('#toast');
    if (!el) return;
    clearTimeout(toastTimer);
    el.textContent = message;
    el.classList.add('is-visible');
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3600);
  }

  function withParam(url, key, value) {
    const u = new URL(url, location.origin);
    u.searchParams.set(key, value);
    return u.pathname + u.search;
  }

  /* ---------- Dialoog ---------- */

  function openDialog(type, content, variant = '') {
    if (!dialog) return;
    if (!dialog.open) savedFocus = document.activeElement;
    dialogType = type;
    dialog.className = 'site-dialog' + (variant ? ' ' + variant : '');
    dialogContent.replaceChildren();
    if (typeof content === 'string') dialogContent.innerHTML = content;
    else dialogContent.append(content);
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('dialog-open');
    dialog.scrollTop = 0;
    requestAnimationFrame(() => {
      const target = $('[autofocus]', dialog) || $('.dialog-close', dialog);
      target?.focus({ preventScroll: true });
    });
    initWishButtons(dialog);
  }

  function closeDialog() { if (dialog?.open) dialog.close(); }

  function fromTemplate(id) {
    const tpl = document.getElementById(id);
    return tpl ? tpl.content.cloneNode(true) : null;
  }

  function loadingBody(title) {
    return `<div class="dialog-body"><div class="dialog-header"><div><h2 id="dialog-title">${title}</h2></div><button type="button" class="icon-button dialog-close" data-action="close" aria-label="Venster sluiten"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></div><p class="micro" role="status">Even laden…</p></div>`;
  }

  if (dialog) {
    dialog.addEventListener('close', () => {
      document.body.classList.remove('dialog-open');
      dialogType = '';
      if (savedFocus?.isConnected) savedFocus.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog();
    });
  }

  /* ---------- Winkelmand ---------- */

  function updateCartCount(count) {
    if (typeof count !== 'number' || Number.isNaN(count)) return;
    $$('.bag-count').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    $$('.bag-button').forEach((el) => el.setAttribute('aria-label', `Winkelmand, ${count} ${count === 1 ? 'artikel' : 'artikelen'}`));
  }

  function applyDrawerHtml(html) {
    const doc = parse(html);
    const body = $('[data-cart-drawer]', doc);
    if (!body) return;
    updateCartCount(Number(body.dataset.itemCount));
    if (dialogType === 'cart') {
      dialogContent.replaceChildren(body);
    }
    return body;
  }

  async function showCart() {
    if (AK.cartType === 'page' && !dialog) { location.href = AK.routes.cart; return; }
    openDialog('cart', loadingBody('Je winkelmand.'));
    try {
      const html = await getText(withParam(AK.routes.cart, 'section_id', 'cart-drawer'));
      const body = applyDrawerHtml(html);
      if (body) $('.dialog-close', dialog)?.focus({ preventScroll: true });
    } catch (err) {
      location.href = AK.routes.cart;
    }
  }

  async function changeLine(line, quantity) {
    const cartPage = $('[data-cart-page]');
    const sections = ['cart-drawer'];
    const pageSection = cartPage?.closest('[id^="shopify-section-"]');
    const pageSectionId = pageSection ? pageSection.id.replace('shopify-section-', '') : null;
    if (pageSectionId) sections.push(pageSectionId);
    const res = await fetch(AK.routes.cartChange + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: sections.join(','), sections_url: location.pathname })
    });
    const data = await res.json();
    if (!res.ok) {
      toast(data.description || data.message || 'Dit aantal is op dit moment niet beschikbaar.');
      return;
    }
    if (data.sections?.['cart-drawer']) applyDrawerHtml(data.sections['cart-drawer']);
    else updateCartCount(data.item_count);
    if (pageSectionId && data.sections?.[pageSectionId]) {
      const fresh = $('[data-cart-page]', parse(data.sections[pageSectionId]));
      if (fresh) cartPage.replaceWith(fresh);
    }
    if (quantity === 0) toast('Het artikel is uit je winkelmand verwijderd.');
  }

  /* ---------- Productpagina: maatkiezer over echte varianten ---------- */

  const pickers = new WeakMap();

  class VariantPicker {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.url = section.dataset.productUrl;
      this.form = $('[data-product-form]', section);
      this.idInput = $('[data-variant-id]', section);
      this.status = $('[data-variant-status]', section);
      this.button = $('[data-add-button]', section);
      this.label = $('[data-add-label]', section);
      this.error = $('[data-form-error]', section);
      this.request = 0;
      this.pending = false;
      this.variant = this.readVariant(section);
      this.bind();
      this.syncFromInputs(true);
    }

    readVariant(root) {
      try { return JSON.parse($('[data-selected-variant]', root)?.textContent || 'null'); } catch { return null; }
    }

    fieldsets() { return $$('[data-option-index]', this.section); }

    selected() {
      return this.fieldsets().map((fs) => {
        const input = $('input:checked', fs);
        return input ? { id: input.dataset.optionValueId, value: input.value } : null;
      });
    }

    bind() {
      this.section.addEventListener('change', (e) => {
        if (!e.target.matches('.size-input')) return;
        const fs = e.target.closest('[data-option-index]');
        const label = fs && $('[data-selected-label]', fs);
        if (label) label.textContent = e.target.value;
        this.syncFromInputs(false);
      });
      this.form?.addEventListener('submit', (e) => this.submit(e));
    }

    missingLabel(sel) {
      const missing = this.fieldsets().filter((fs, i) => !sel[i]).map((fs) => fs.dataset.optionName.toLowerCase());
      if (!missing.length) return '';
      if (missing.length === 1) return `Kies je ${missing[0]}.`;
      return `Kies je ${missing.slice(0, -1).join(', ')} en ${missing.at(-1)}.`;
    }

    setStatus(text, isError = false) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.classList.toggle('error', isError);
    }

    setButton(enabled, text) {
      if (!this.button) return;
      this.button.disabled = !enabled;
      if (this.label && text) this.label.textContent = text;
    }

    async syncFromInputs(initial) {
      const sel = this.selected();
      const complete = sel.every(Boolean);
      if (this.error) this.error.hidden = true;

      // Eerste keer laden zonder volledige keuze: niets opvragen, alleen uitnodigen tot kiezen.
      if (initial && !complete) {
        if (this.idInput) this.idInput.value = '';
        this.setButton(true, 'In je winkelmand');
        this.setStatus(this.missingLabel(sel));
        return;
      }
      if (initial && complete && this.variant && this.matches(this.variant, sel)) {
        this.applyVariant(this.variant, sel);
        return;
      }

      const ids = sel.filter(Boolean).map((s) => s.id);
      const ticket = ++this.request;
      this.section.setAttribute('aria-busy', 'true');
      try {
        let url = withParam(this.url, 'section_id', this.sectionId);
        if (ids.length) url = withParam(url, 'option_values', ids.join(','));
        const html = await getText(url);
        if (ticket !== this.request) return; // er is intussen opnieuw gekozen
        const doc = parse(html);
        this.refreshAvailability(doc, sel);
        const variant = this.readVariant(doc);
        this.copyDelivery(complete ? doc : null);
        if (!complete) {
          if (this.idInput) this.idInput.value = '';
          this.setButton(true, 'In je winkelmand');
          this.setStatus(this.missingLabel(sel));
          return;
        }
        if (!variant || !this.matches(variant, sel)) {
          // Shopify gaf een andere variant terug: deze combinatie bestaat niet. Niet stilzwijgend vervangen.
          this.copyDelivery(null);
          if (this.idInput) this.idInput.value = '';
          this.setButton(false, 'Niet leverbaar');
          this.setStatus('Deze maatcombinatie bestaat niet voor dit model. Kies een andere maat.', true);
          return;
        }
        this.applyVariant(variant, sel, doc);
      } catch (err) {
        if (ticket !== this.request) return;
        this.setStatus('De beschikbaarheid kon niet worden opgehaald. Controleer je verbinding en probeer het opnieuw.', true);
      } finally {
        if (ticket === this.request) this.section.removeAttribute('aria-busy');
      }
    }

    matches(variant, sel) {
      const opts = variant.options || [];
      return sel.every((s, i) => s && String(opts[i]) === String(s.value));
    }

    copyDelivery(doc) {
      const cur = $('[data-variant-delivery]', this.section);
      if (!cur) return;
      const fresh = doc && $('[data-variant-delivery]', doc);
      cur.innerHTML = fresh ? fresh.innerHTML : '';
    }

    refreshAvailability(doc, sel) {
      // Neem beschikbaarheid over uit Shopify's antwoord, maar behoud de keuzes van de klant.
      this.fieldsets().forEach((fs, i) => {
        const freshFs = $(`[data-option-index="${i}"]`, doc);
        if (!freshFs) return;
        $$('.size-input', fs).forEach((input) => {
          const fresh = $(`[data-option-value-id="${input.dataset.optionValueId}"]`, freshFs);
          if (!fresh) return;
          input.disabled = fresh.disabled && !(sel[i] && sel[i].id === input.dataset.optionValueId);
          const available = fresh.dataset.available === 'true';
          input.dataset.available = String(available);
          const label = $(`label[for="${input.id}"]`, fs);
          const freshLabel = $(`label[for="${input.id}"]`, freshFs);
          if (label && freshLabel) {
            // Klassen en verborgen tekst (uitverkocht / levertijd) komen uit Shopify's antwoord.
            label.className = freshLabel.className;
            label.innerHTML = freshLabel.innerHTML;
          } else if (label) {
            label.classList.toggle('is-unavailable', !available);
          }
        });
      });
    }

    applyVariant(variant, sel, doc) {
      this.variant = variant;
      if (this.idInput) this.idInput.value = variant.id;
      const size = sel.map((s) => s.value).join(' / ');
      if (variant.available) {
        this.setButton(true, 'In je winkelmand');
        this.setStatus(`${size} is beschikbaar.`);
      } else {
        this.setButton(false, 'Uitverkocht');
        this.setStatus(`${size} is op dit moment uitverkocht.`, false);
      }
      if (doc) {
        const price = $('[data-price]', doc);
        const target = $('[data-price]', this.section);
        if (price && target) target.innerHTML = price.innerHTML;
        ['[data-variant-sku]', '[data-variant-barcode]'].forEach((sel2) => {
          const fresh = $(sel2, doc); const cur = $(sel2, this.section);
          if (fresh && cur) cur.textContent = fresh.textContent;
        });
      }
      if (variant.featured_media?.id) this.showMedia(variant.featured_media.id);
      const u = new URL(location.href);
      u.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      this.section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    showMedia(mediaId) {
      const thumb = $(`[data-action="media"][data-media-id="${mediaId}"]`, this.section);
      if (thumb) swapMedia(thumb);
    }

    async submit(e) {
      e.preventDefault();
      if (this.pending) return; // nooit dubbel toevoegen
      const sel = this.selected();
      if (this.fieldsets().length && !sel.every(Boolean)) {
        this.setStatus(this.missingLabel(sel), true);
        const firstEmpty = this.fieldsets().find((fs, i) => !sel[i]);
        $('input:not(:disabled)', firstEmpty)?.focus();
        return;
      }
      if (!this.idInput?.value) {
        this.setStatus('Kies eerst een beschikbare maat om dit artikel toe te voegen.', true);
        return;
      }
      this.pending = true;
      this.button?.setAttribute('aria-busy', 'true');
      if (this.button) this.button.disabled = true;
      if (this.error) this.error.hidden = true;

      const formData = new FormData(this.form);
      formData.set('id', this.idInput.value);
      formData.set('sections', 'cart-drawer');
      formData.set('sections_url', location.pathname);
      try {
        const res = await fetch(AK.routes.cartAdd + '.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // 422: Shopify weigert (bijv. voorraad veranderd). Laat de echte melding zien.
          const msg = data.description || data.message || 'Dit artikel kon niet worden toegevoegd.';
          if (this.error) { this.error.textContent = msg; this.error.hidden = false; }
          this.setStatus(msg, true);
          return;
        }
        if (data.sections?.['cart-drawer']) {
          const body = parse(data.sections['cart-drawer']);
          const count = Number($('[data-cart-drawer]', body)?.dataset.itemCount);
          updateCartCount(count);
        }
        toast(`${data.product_title || 'Het artikel'} is toegevoegd aan je winkelmand.`);
        if (AK.cartType === 'page') location.href = AK.routes.cart;
        else if (data.sections?.['cart-drawer']) { openDialog('cart', ''); applyDrawerHtml(data.sections['cart-drawer']); }
        else showCart();
      } catch (err) {
        // Netwerkfout: onduidelijk of het gelukt is. Niet opnieuw versturen, maar de echte winkelmand tonen.
        this.setStatus('De verbinding viel weg. We tonen je winkelmand zodat je kunt zien of het artikel is toegevoegd.', true);
        showCart();
      } finally {
        this.pending = false;
        this.button?.removeAttribute('aria-busy');
        if (this.button && this.variant?.available !== false && this.idInput?.value) this.button.disabled = false;
      }
    }
  }

  function swapMedia(thumb) {
    const section = thumb.closest('[data-product-section]');
    const main = $('[data-main-media] img', section);
    if (!main) return;
    main.src = thumb.dataset.src;
    if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
    const alt = $('img', thumb)?.getAttribute('data-alt');
    if (alt) main.alt = alt;
    $$('[data-action="media"]', section).forEach((b) => b.removeAttribute('aria-current'));
    thumb.setAttribute('aria-current', 'true');
  }

  function zoom(button) {
    const section = button.closest('[data-product-section]');
    const img = $('[data-main-media] img', section);
    if (!img) return;
    const src = img.currentSrc || img.src;
    const big = src.replace(/([?&])width=\d+/, '$1width=2400');
    const title = $('h1', section)?.textContent || 'Productfoto';
    openDialog('zoom', `<div class="dialog-body"><div class="dialog-header"><div><h2 id="dialog-title">${title.replace(/</g, '&lt;')}</h2></div><button type="button" class="icon-button dialog-close" data-action="close" aria-label="Venster sluiten"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></div><div class="media zoom-media"><img src="${big}" alt="${(img.alt || '').replace(/"/g, '&quot;')} — vergroot" loading="eager"></div></div>`, 'dialog-zoom');
  }

  /* ---------- Favorieten (alleen handles, nooit prijs of voorraad) ---------- */

  const FAV_KEY = 'ak-favorieten-v1';
  let favStore = true;
  function readFavs() {
    try {
      const list = JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
      return Array.isArray(list) ? [...new Set(list.filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/.test(h)))].slice(0, 100) : [];
    } catch { favStore = false; return []; }
  }
  function writeFavs(list) { try { localStorage.setItem(FAV_KEY, JSON.stringify(list)); } catch { favStore = false; } }

  function initWishButtons(root = document) {
    const favs = readFavs();
    $$('[data-action="wish"]', root).forEach((b) => {
      const on = favs.includes(b.dataset.handle);
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-label', `${on ? 'Verwijder' : 'Bewaar'} ${b.dataset.title || 'product'} ${on ? 'uit' : 'bij'} favorieten`);
    });
    $$('.wish-count').forEach((el) => { el.textContent = favs.length; el.hidden = !favs.length; });
    $$('.wish-link').forEach((el) => el.setAttribute('aria-label', `Favorieten, ${favs.length} ${favs.length === 1 ? 'artikel' : 'artikelen'}`));
  }

  function toggleWish(button) {
    const handle = button.dataset.handle;
    if (!handle) return;
    let favs = readFavs();
    const on = favs.includes(handle);
    favs = on ? favs.filter((h) => h !== handle) : [...favs, handle];
    writeFavs(favs);
    initWishButtons();
    toast(`${button.dataset.title || 'Het artikel'} ${on ? 'verwijderd uit' : 'bewaard bij'} je favorieten.${favStore ? '' : ' Je browser staat opslaan niet toe; de lijst verdwijnt na deze sessie.'}`);
    const page = $('[data-favorites]');
    if (page && on) renderFavorites(page);
  }

  async function renderFavorites(container) {
    const grid = $('[data-favorites-grid]', container);
    const empty = $('[data-favorites-empty]', container);
    const clear = $('[data-action="clear-favorites"]', container);
    const favs = readFavs();
    grid.replaceChildren();
    empty.hidden = favs.length > 0;
    if (clear) clear.hidden = favs.length === 0;
    if (!favs.length) return;
    const root = (AK.routes.root || '/').replace(/\/$/, '');
    const results = await Promise.all(favs.map(async (handle) => {
      try {
        const html = await getText(`${root}/products/${encodeURIComponent(handle)}?section_id=favorite-card`);
        return $('.product-card', parse(html));
      } catch (err) {
        return err.status === 404 ? 'gone:' + handle : null;
      }
    }));
    // Producten die niet meer bestaan netjes uit de lijst halen.
    const gone = results.filter((r) => typeof r === 'string').map((r) => r.slice(5));
    if (gone.length) writeFavs(readFavs().filter((h) => !gone.includes(h)));
    results.filter((r) => r instanceof Element).forEach((card) => grid.append(card));
    empty.hidden = grid.children.length > 0;
    initWishButtons(container);
  }

  /* ---------- Zoeken ---------- */

  let searchTimer;
  let searchAbort;
  function openSearch() {
    const frag = fromTemplate('tpl-search');
    if (!frag) { location.href = AK.routes.search || '/search'; return; }
    openDialog('search', frag);
  }
  async function predictive(q) {
    const out = $('#predictive-results');
    if (!out) return;
    const term = q.trim();
    if (term.length < 2) { out.replaceChildren(); return; }
    searchAbort?.abort();
    searchAbort = new AbortController();
    try {
      const url = `${AK.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
      const html = await getText(url, { signal: searchAbort.signal });
      const section = parse(html).querySelector('.shopify-section') || parse(html).body;
      out.innerHTML = section.innerHTML;
    } catch (err) {
      if (err.name !== 'AbortError') out.innerHTML = '<p class="search-empty">Zoeksuggesties zijn even niet beschikbaar. Druk op Enter om te zoeken.</p>';
    }
  }

  /* ---------- Collectie: filters en sorteren via Section Rendering ---------- */

  function collectionSection(el) { return el?.closest?.('[data-collection-section]') || $('[data-collection-section]'); }

  async function renderCollection(url, push = true) {
    const section = $('[data-collection-section]');
    if (!section) { location.href = url; return; }
    const id = section.dataset.sectionId;
    const results = $('[data-collection-results]', section);
    results?.setAttribute('aria-busy', 'true');
    try {
      const html = await getText(withParam(url, 'section_id', id));
      const fresh = $('[data-collection-results]', parse(html));
      if (fresh && results) results.replaceWith(fresh);
      if (push) history.pushState({ collection: true }, '', url);
      initWishButtons(section);
      const count = $('.filter-count', section);
      if (count) count.focus?.();
    } catch (err) {
      location.href = url;
    }
  }

  function formToUrl(form) {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form).entries()) if (String(v).trim() !== '') params.append(k, v);
    const base = form.getAttribute('action') || location.pathname;
    return base + (params.toString() ? '?' + params.toString() : '');
  }

  function openFilters(trigger) {
    const section = collectionSection(trigger);
    const panel = section && $('[data-filter-panel]', section);
    if (!panel) return false;
    const clone = panel.firstElementChild.cloneNode(true);
    // Unieke id's in de kloon, zodat labels blijven werken.
    $$('[id]', clone).forEach((el) => {
      if (el.id === 'dialog-title') return;
      const old = el.id; el.id = 'dlg-' + old;
      $$(`label[for="${old}"]`, clone).forEach((l) => l.setAttribute('for', el.id));
    });
    openDialog('filters', clone);
    return true;
  }

  window.addEventListener('popstate', () => {
    if ($('[data-collection-section]')) renderCollection(location.pathname + location.search, false);
  });

  /* ---------- Aanbevelingen ---------- */

  async function initRecommendations(root = document) {
    for (const el of $$('[data-recommendations]', root)) {
      if (el.dataset.loaded) continue;
      el.dataset.loaded = '1';
      try {
        const html = await getText(el.dataset.url);
        const fresh = $('[data-recommendations]', parse(html));
        if (fresh && $('.product-card', fresh)) { el.innerHTML = fresh.innerHTML; el.hidden = false; initWishButtons(el); }
      } catch { /* aanbevelingen zijn optioneel */ }
    }
  }

  /* ---------- Initialisatie per sectie ---------- */

  function init(root = document) {
    $$('[data-product-section]', root).forEach((s) => { if (!pickers.has(s)) pickers.set(s, new VariantPicker(s)); });
    $$('[data-favorites]', root).forEach((c) => renderFavorites(c));
    initRecommendations(root);
    initWishButtons(root);
  }

  /* ---------- Globale events ---------- */

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-action], [data-filter-link]');
    if (!el) return;
    if (el.hasAttribute('data-filter-link') && !el.dataset.action) {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      closeDialog();
      renderCollection(el.getAttribute('href'));
      return;
    }
    const action = el.dataset.action;
    switch (action) {
      case 'close': e.preventDefault(); closeDialog(); break;
      case 'menu': { const f = fromTemplate('tpl-menu'); if (f) { e.preventDefault(); openDialog('menu', f); } break; }
      case 'search': e.preventDefault(); openSearch(); break;
      case 'cart':
        if (AK.cartType === 'page' || location.pathname === AK.routes.cart) return;
        e.preventDefault(); showCart(); break;
      case 'size-guide': { const f = fromTemplate('tpl-size-guide'); if (f) { e.preventDefault(); openDialog('size-guide', f); } break; }
      case 'filters': if (openFilters(el)) e.preventDefault(); break;
      case 'wish': e.preventDefault(); toggleWish(el); break;
      case 'media': e.preventDefault(); swapMedia(el); break;
      case 'zoom': e.preventDefault(); zoom(el); break;
      case 'quick': {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        openDialog('quick', loadingBody('Even van dichtbij.'), 'dialog-wide');
        getText(withParam(el.dataset.url, 'section_id', 'quick-view'))
          .then((html) => { const body = $('.dialog-body', parse(html)); if (body && dialogType === 'quick') { dialogContent.replaceChildren(body); $('.dialog-close', dialog)?.focus(); } })
          .catch(() => { location.href = el.dataset.url; });
        break;
      }
      case 'quantity':
        e.preventDefault();
        if (el.getAttribute('aria-disabled') === 'true') return;
        changeLine(el.dataset.line, Number(el.dataset.quantity)).catch(() => { location.href = el.getAttribute('href'); });
        break;
      case 'search-term': {
        const input = $('#dialog-search-input');
        if (input) { input.value = el.dataset.value; input.focus(); predictive(input.value); }
        break;
      }
      case 'clear-favorites': writeFavs([]); initWishButtons(); { const p = $('[data-favorites]'); if (p) renderFavorites(p); } toast('Je favorieten zijn gewist.'); break;
      default: break;
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target.id === 'dialog-search-input') {
      clearTimeout(searchTimer);
      const q = e.target.value;
      searchTimer = setTimeout(() => predictive(q), 180);
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-sort-select]')) {
      renderCollection(formToUrl(e.target.form));
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-filter-form]')) {
      e.preventDefault();
      closeDialog();
      renderCollection(formToUrl(form));
    } else if (form.matches('[data-sort-form]')) {
      e.preventDefault();
      renderCollection(formToUrl(form));
    }
  });

  window.addEventListener('storage', (e) => { if (e.key === FAV_KEY) { initWishButtons(); const p = $('[data-favorites]'); if (p) renderFavorites(p); } });

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', () => closeDialog());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
